import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  NorthEast as NorthEastIcon,
  SouthWest as SouthWestIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { InfoField } from '../../components';
import { ConfirmModal, ConfirmModalAction } from './components';

import { UTCToDate, UTCToDateTime, UTCToDateTimeWithSecond } from '../../utils/dates';
import { RouterPaths } from '../../router/paths';
import { Blockchain, PaymentMethod, TransactionDirection, TransactionStatus } from '../../enums';
import {
  PostTransactionDocumentsDTO,
  useCancelAmlTransactionMutation,
  useCancelTransactionMutation,
  useConfirmAmlTransactionMutation,
  useDeleteTransactionDocumentByIdMutation,
  useExecuteTransactionMutation,
  useGetTransactionDocumentsQuery,
  useGetTransactionQuery,
  useLazyGetAmlCheckQuery,
  useLazyGetProfileQuery,
  useLazyGetTransactionExchangeOrderQuery,
  usePostTransactionDocumentMutation,
} from '../../api';
import {
  formatAmount,
  formatBlockchainAmountWithCurrency,
  formatFiatAmountWithCurrency,
} from '../../utils';
import { AmlConfirmModal } from './components/AmlConfirmModal';
import { AmlBlock } from './components/AmlBlock';
import { permissionsCheck } from '../../utils/permission';
import { UploadDocumentModal } from './components/UploadDocumentModal';
import { ViewDocumentModal } from '../../components/ViewDocumentModal';
import { DeleteConfirmModal } from './components/DeleteConfirmModal';
import {
  useDownloadWithdrawalOrderPdfMutation,
  useDownloadWithdrawalOrderDocxMutation,
} from '../../api/controllers/docgen/docgen';

export const Transaction: FC = () => {
  const { t } = useTranslation();
  const { transactionId } = useParams<'transactionId'>();
  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const hasPermissionToReadAmlChecks = permissionsCheck(['READ_AML_CHECKS']);
  const hasPermissionToCancelAmlChecks = permissionsCheck(['AML_CANCEL_TRANSACTION']);
  const hasPermissionToСonfirmAmlChecks = permissionsCheck(['AML_CONFIRM_TRANSACTION']);

  const routerPathsValues = Object.values(RouterPaths);
  const activePath = routerPathsValues.filter((path) => matchPath(path, pathname))[0];
  const activePathKey = Object.keys(RouterPaths)[Object.values(RouterPaths).indexOf(activePath)];
  // api
  const {
    data: transaction,
    isLoading: isTransactionLoading,
    isSuccess: isTransactionSuccess,
  } = useGetTransactionQuery(transactionId ? { transactionId } : skipToken);
  const { data: documents, refetch: refetchDocuments } = useGetTransactionDocumentsQuery(
    transactionId ? { transactionId } : skipToken
  );
  const [createDocument, { isLoading: isDocumentUploadLoading }] =
    usePostTransactionDocumentMutation();
  const [deleteDocument, { isLoading: isDocumentDeleteLoading }] =
    useDeleteTransactionDocumentByIdMutation();
  const [executeTransaction, { isLoading: isExecuteTransactionLoading }] =
    useExecuteTransactionMutation();
  const [cancelTransaction, { isLoading: isCancelTransactionLoading }] =
    useCancelTransactionMutation();
  const [confirmAmlTransaction, { isLoading: isConfirmAmlTransactionLoading }] =
    useConfirmAmlTransactionMutation();
  const [cancelAmlTransaction, { isLoading: isCancelAmlTransactionLoading }] =
    useCancelAmlTransactionMutation();
  const [getProfile, { data: profile }] = useLazyGetProfileQuery();
  const [downloadWithdrawalOrderPdf, { isLoading: isDownloadWithdrawalOrderPdfLoading }] =
    useDownloadWithdrawalOrderPdfMutation();
  const [downloadWithdrawalOrderDocx, { isLoading: isDownloadWithdrawalOrderDocxLoading }] =
    useDownloadWithdrawalOrderDocxMutation();
  const [
    getTransactionExchangeOrder,
    { data: exchangeOrder, isLoading: isTransactionExchangeOrderLoading },
  ] = useLazyGetTransactionExchangeOrderQuery();

  useEffect(() => {
    if (transaction) {
      getProfile({ profileUid: transaction.transaction.profile_uid });
    }
  }, [transaction]);

  const [selectredFile, setSelectedFile] = useState<{ url: string; fileType: string } | null>(null);
  const [openViewDocument, setOpenViewDocument] = useState(false);

  const [activeConfirmAction, setActiveConfirmAction] = useState<ConfirmModalAction>('');
  const [amlConfirmModalShown, setAmlConfirmModalShown] = useState(false);
  const openAmlConfirmModal = (confirmAction: ConfirmModalAction) => {
    setAmlConfirmModalShown(true);
    setActiveConfirmAction(confirmAction);
  };
  const downloadPdf = (type: 'PDF' | 'DOCX') => {
    const arg = {
      current_date: UTCToDate(new Date().toISOString()),
      number: transaction?.transaction.id ?? '',
      client_name: profile?.profile.name ?? '',
      client_id: profile?.profile.reference_number ?? '',
      client_email: profile?.profile.user_info.traits.email,
      client_phone: profile?.profile.user_info.traits.phone,
      withdrawal_amount: Math.abs(transaction?.transaction.amount ?? 0).toString(),
      withdrawal_date: UTCToDateTime(transaction?.transaction.created_at),
      withdrawal_reference: transaction?.transaction.reference ?? '',
      acc_beneficiary_name: transaction?.transaction.transfer_info?.beneficiary_name ?? '',
      acc_bank_name: transaction?.transaction.transfer_info?.bank_name ?? '',
      acc_iban: transaction?.transaction.transfer_info?.account ?? '',
      acc_swift: transaction?.transaction.transfer_info?.swift ?? '',
      acc_bank_address: transaction?.transaction.transfer_info?.bank_address ?? '',
    };

    if (type === 'PDF') {
      downloadWithdrawalOrderPdf(arg);
    } else {
      downloadWithdrawalOrderDocx(arg);
    }
  };
  const closeAmlConfirmModal = () => {
    setAmlConfirmModalShown(false);
    setActiveConfirmAction('');
  };

  const returnTo = () => {
    if (activePathKey === 'Transaction') {
      return RouterPaths.Transactions;
    }

    if (activePathKey === 'ProfileTransaction') {
      return pathname.split('/').slice(0, -1).join('/');
    }
    return pathname.split('/').slice(0, -2).join('/');
  };

  useEffect(() => {
    if (isTransactionLoading || !isTransactionSuccess || !transactionId) return;
    if (transaction.transaction.payment_method === PaymentMethod.EXCHANGE) {
      getTransactionExchangeOrder({ transactionId });
    }
  }, [isTransactionLoading, isTransactionSuccess]);

  // variables
  const isDisplayActionBtns =
    transaction?.transaction.direction === TransactionDirection.OUT &&
    transaction?.transaction.status === TransactionStatus.PENDING &&
    transaction?.transaction.coin.blockchain === Blockchain.FIAT &&
    transaction?.transaction.payment_method !== PaymentMethod.EXCHANGE &&
    transaction?.transaction.payment_method !== PaymentMethod.BLOCKCHAIN;
  const isDisplayAmlActionBtns =
    transaction?.transaction.status === TransactionStatus.PENDING_CONFIRMATION;
  const isDisplayDownloadBtns =
    transaction?.transaction.direction === TransactionDirection.OUT &&
    transaction?.transaction.payment_method === PaymentMethod.BANK;
  const isLoading =
    isTransactionLoading ||
    isExecuteTransactionLoading ||
    isCancelTransactionLoading ||
    isTransactionExchangeOrderLoading ||
    isDownloadWithdrawalOrderPdfLoading ||
    isDownloadWithdrawalOrderDocxLoading;

  // confirm modals logic
  const [confirmModalShown, setConfirmModalShown] = useState(false);
  const openConfirmModal = (confirmAction: ConfirmModalAction) => {
    setConfirmModalShown(true);
    setActiveConfirmAction(confirmAction);
  };
  const closeConfirmModal = () => {
    setConfirmModalShown(false);
    setActiveConfirmAction('');
  };
  // add document modal logic
  const [uploadModalShown, setUploadModalShown] = useState(false);
  const openUploadModal = () => {
    setUploadModalShown(true);
  };
  const closeUploadModal = () => {
    setUploadModalShown(false);
    setEditFields(null);
  };

  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  // delete modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (id: string) => {
    setDeleteModalShown(true);
    setActiveDocumentId(id);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveDocumentId(null);
  };
  // fields logic
  const [editFields, setEditFields] = useState<PostTransactionDocumentsDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    isFile?: boolean
  ) => {
    const name = e.target.name;
    // @ts-ignore
    const value = isFile ? e.target.files[0] : e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // api handlers
  const uploadDocument = async () => {
    if (!editFields || !transactionId) return;

    const formData = new FormData();
    formData.append('file', editFields.file);
    formData.append('type', editFields.type);

    const args = { transactionId, body: formData };

    try {
      // @ts-ignore
      await createDocument(args).unwrap();
      closeUploadModal();
      refetchDocuments();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const handleDeleteDocument = async () => {
    if (!activeDocumentId || !transactionId) return;

    const args = { transactionId, documentId: activeDocumentId };

    try {
      await deleteDocument(args).unwrap();
      closeDeleteModal();
      refetchDocuments();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const executeAction = async (value?: string) => {
    if (!transaction?.transaction || activeConfirmAction === '') return;

    const args = {
      transactionId: transaction.transaction.id,
      body: value
        ? activeConfirmAction === 'cancel'
          ? { status_reason: value }
          : { ext_transaction_id: value }
        : undefined,
    };
    const request = activeConfirmAction === 'execute' ? executeTransaction : cancelTransaction;

    try {
      // @ts-ignore
      await request(args).unwrap();
      closeConfirmModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const executeAmlAction = async (reason?: string, password?: string) => {
    if (!transaction?.transaction || activeConfirmAction === '' || !password || !reason) return;

    const args = {
      transactionId: transaction.transaction.id,
      password: password,
      body: { status_reason: reason },
    };
    const request =
      activeConfirmAction === 'execute' ? confirmAmlTransaction : cancelAmlTransaction;

    try {
      await request(args).unwrap();
      closeAmlConfirmModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const getPath = (path: string) => {
    if (path.startsWith('http') || path.startsWith('https')) {
      return path;
    }

    return `https://${path}`;
  };
  const renderObject = (obj: object, withoutHash?: boolean) => {
    return Object.keys(obj).map((key) => {
      // @ts-ignore
      const value = obj[key];

      if (key === 'hash' && !withoutHash) {
        const cryptoExporerLink =
          transaction?.transaction.coin.blockchain === Blockchain.BTC
            ? process.env.REACT_APP_BLOCKCHAIN_EXPLORER_BTC_URL
            : transaction?.transaction.coin.blockchain === Blockchain.ETH
              ? process.env.REACT_APP_BLOCKCHAIN_EXPLORER_ETH_URL
              : `${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_TRX_URL}#/transaction/`;

        return (
          <Stack direction="row" key={key}>
            <Typography color="darkslategray" sx={{ whiteSpace: 'break-spaces' }}>
              {`${key}: `}
            </Typography>

            <MuiLink underline="hover" component={Link} to={`${cryptoExporerLink}${value}`}>
              <div style={{ wordBreak: 'break-all' }}>{value}</div>
            </MuiLink>
          </Stack>
        );
      }

      if (typeof value === 'object' && value !== null) {
        return (
          <Stack key={key}>
            <Typography color="darkslategray" sx={{ whiteSpace: 'break-spaces' }}>
              {`${key}: `}
            </Typography>
            <Box pl={1.5}>{renderObject(value, true)}</Box>
          </Stack>
        );
      }

      return (
        <Stack direction="row" key={key}>
          <Typography color="darkslategray" sx={{ whiteSpace: 'break-spaces' }}>
            {`${key}: `}
          </Typography>
          <div style={{ wordBreak: 'break-all' }}>{value}</div>
        </Stack>
      );
    });
  };

  const documentsBlock = useMemo(() => {
    return (
      <Stack
        sx={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'column',
          gap: '10px',
          alignItems: 'flex-start',
        }}
      >
        {documents?.documents.map((document) => (
          <Stack
            key={document.id}
            sx={{
              maxWidth: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <div>{UTCToDateTime(document.created_at)}</div>
            <div>{t(`common.document-type.${document.type}`)}</div>
            <MuiLink
              to={''}
              underline="hover"
              component={Link}
              onClick={() => {
                const splitFileName = document.path?.split('.');
                const fileType = splitFileName[splitFileName.length - 1];
                setSelectedFile({
                  url: getPath(document.path),
                  fileType: fileType,
                });
                setOpenViewDocument(true);
              }}
            >
              {document.path.split('/')[document.path.split('/').length - 1]}
            </MuiLink>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={() => openDeleteModal(document.id)}
              disabled={isDocumentDeleteLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="open drawer"
          onClick={() => openUploadModal()}
        >
          <AttachFileIcon />
        </IconButton>
      </Stack>
    );
  }, [documents]);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2} pl={3} pt={1} width="100%">
        <MuiLink underline="hover" component={Link} to={returnTo()}>
          <Stack direction="row" alignItems="stretch" spacing={0.2}>
            <ArrowBackIcon fontSize="inherit" />
            <Typography variant="caption" component="span">
              {t(`transaction.action-btn.back-to-${activePathKey}`)}
            </Typography>
          </Stack>
        </MuiLink>

        <Divider orientation="vertical" />

        <Typography color="black">#{transaction?.transaction.id}</Typography>

        <Stack direction="row" justifyContent="flex-end" spacing={2} flexGrow={1}>
          {isDisplayDownloadBtns && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={() => downloadPdf('PDF')}
              >
                {t('transaction.action-btn.download-withdrawal-pdf')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={() => downloadPdf('DOCX')}
              >
                {t('transaction.action-btn.download-withdrawal-docx')}
              </Button>
            </>
          )}

          {isDisplayActionBtns && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
                disabled={isLoading}
                onClick={() => openConfirmModal('execute')}
              >
                {t('transaction.action-btn.execute')}
              </Button>
              <Button
                color="error"
                startIcon={<BlockIcon />}
                disabled={isLoading}
                onClick={() => openConfirmModal('cancel')}
              >
                {t('transaction.action-btn.cancel')}
              </Button>
            </>
          )}

          {isDisplayAmlActionBtns && hasPermissionToСonfirmAmlChecks && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckIcon />}
              disabled={isLoading}
              onClick={() => openAmlConfirmModal('execute')}
            >
              {t('transaction.action-btn.execute')}
            </Button>
          )}
          {isDisplayAmlActionBtns && hasPermissionToCancelAmlChecks && (
            <Button
              color="error"
              startIcon={<BlockIcon />}
              disabled={isLoading}
              onClick={() => openAmlConfirmModal('cancel')}
            >
              {t('transaction.action-btn.cancel')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
        <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography pb={2}>{t('transaction.label.transfer-info')}</Typography>

          <InfoField
            name={t('transaction.created-at')}
            value={transaction?.transaction.created_at}
            formattedValue={
              transaction?.transaction.created_at
                ? UTCToDateTimeWithSecond(transaction.transaction.created_at as string)
                : undefined
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.finished-at')}
            value={transaction?.transaction.finished_at}
            formattedValue={
              transaction?.transaction.finished_at
                ? UTCToDateTimeWithSecond(transaction.transaction.finished_at as string)
                : undefined
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.id')}
            value={transaction?.transaction.id}
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.linked-out-transaction-id')}
            value={
              transaction?.transaction.transfer_info?.linked_out_transaction_id as
                | string
                | undefined
            }
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Transactions}/${transaction?.transaction.transfer_info?.linked_out_transaction_id}`}
              >
                <Typography>
                  {
                    transaction?.transaction.transfer_info?.linked_out_transaction_id as
                      | string
                      | undefined
                  }
                </Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.linked-in-transaction-id')}
            value={
              transaction?.transaction.transfer_info?.linked_in_transaction_id as string | undefined
            }
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Transactions}/${transaction?.transaction.transfer_info?.linked_in_transaction_id}`}
              >
                <Typography>
                  {
                    transaction?.transaction.transfer_info?.linked_in_transaction_id as
                      | string
                      | undefined
                  }
                </Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.wallet-uid')}
            value={transaction?.transaction.wallet_uid}
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Profiles}/${transaction?.transaction.profile_uid}/wallets`}
              >
                <Typography>{transaction?.transaction.wallet_uid}</Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.coin')}
            value={transaction?.transaction.coin.name}
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Profiles}/${transaction?.transaction.profile_uid}/wallets`}
              >
                <Typography>
                  {transaction?.transaction.coin.name}{' '}
                  {transaction?.transaction.merchant_deposit_request_uid && (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: '#1976d2',
                        padding: '2px 4px',
                        borderRadius: '8px',
                        color: 'white',
                        ml: 1,
                        fontSize: 12,
                      }}
                    >
                      {t('transaction.merchant')}
                    </Box>
                  )}
                </Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.status')}
            value={transaction?.transaction.status}
            formattedValue={
              transaction?.transaction.status &&
              t(`common.transaction-status.${transaction.transaction.status}`)
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.status-reason')}
            value={transaction?.transaction.status_reason}
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.payment-method')}
            value={transaction?.transaction.payment_method}
            formattedValue={
              transaction?.transaction.payment_method &&
              t(`common.payment-method.${transaction.transaction.payment_method}`)
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.provider')}
            value={transaction?.transaction.provider}
            nameSx={{ width: '130px' }}
            withEmptyValue
          />

          <InfoField
            name={t('transaction.ext-transaction-id')}
            value={transaction?.transaction.ext_transaction_id}
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.direction')}
            value={transaction?.transaction.direction}
            component={
              transaction?.transaction.direction &&
              (transaction.transaction.direction === TransactionDirection.OUT ? (
                <Stack direction="row" gap="4px">
                  <NorthEastIcon color="error" fontSize="small" />
                  {t(`common.transaction-direction.${transaction.transaction.direction}`)}
                </Stack>
              ) : (
                <Stack direction="row" gap="4px">
                  <SouthWestIcon color="success" fontSize="small" />
                  {t(`common.transaction-direction.${transaction.transaction.direction}`)}
                </Stack>
              ))
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.amount')}
            value={undefined}
            withEmptyValue
            formattedValue={formatAmount(transaction?.transaction.amount)}
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.fee')}
            value={undefined}
            formattedValue={formatAmount(transaction?.transaction.fee)}
            nameSx={{ width: '130px' }}
            withEmptyValue
          />

          <InfoField
            name={t('transaction.profile-uid')}
            value={transaction?.transaction.profile_uid}
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Profiles}/${transaction?.transaction.profile_uid}`}
              >
                <Typography>{transaction?.transaction.profile_uid}</Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.profile-name')}
            value={transaction?.transaction.profile_name}
            component={
              <MuiLink
                underline="hover"
                component={Link}
                to={`${RouterPaths.Profiles}/${transaction?.transaction.profile_uid}`}
              >
                <Typography>{transaction?.transaction.profile_name}</Typography>
              </MuiLink>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.transfer-info')}
            // @ts-ignore
            value={transaction?.transaction.transfer_info}
            component={
              <Stack direction="column" spacing={1.5}>
                {transaction?.transaction.transfer_info
                  ? renderObject(transaction.transaction.transfer_info)
                  : '–'}
              </Stack>
            }
            nameSx={{ width: '130px' }}
          />

          <InfoField
            name={t('transaction.reference')}
            value={transaction?.transaction.reference}
            withEmptyValue
          />

          <InfoField
            name={t('transaction.documents')}
            value={transaction?.transaction.reference}
            component={documentsBlock}
            maxWidthValue="100%"
            withEmptyValue
            withoutDivider
          />
        </Paper>
      </Grid>

      {exchangeOrder?.order && (
        <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
          <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography pb={2}>{t('transaction.label.order-info')}</Typography>

            <InfoField
              name={t('transaction.order.state')}
              value={t(`common.transaction-order-status.${exchangeOrder.order.state}`)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.id')}
              value={exchangeOrder.order.id}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.provider')}
              value={exchangeOrder.order.provider}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.provider-order-id')}
              value={exchangeOrder.order.provider_order_id}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.symbol')}
              value={exchangeOrder.order.symbol}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.side')}
              value={t(`common.transaction-order-status.${exchangeOrder.order.side}`)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.volume')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.volume)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.cost')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.cost)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.fee')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.fee)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.req-price')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.req_price)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.req-result')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.req_result)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.filled-price')}
              value={undefined}
              withEmptyValue
              formattedValue={formatAmount(exchangeOrder.order.filled_price)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.filled-at')}
              value={exchangeOrder.order.filled_at}
              formattedValue={UTCToDateTimeWithSecond(exchangeOrder.order.filled_at as string)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.created-at')}
              value={exchangeOrder.order.created_at}
              formattedValue={UTCToDateTimeWithSecond(exchangeOrder.order.created_at as string)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.transaction-id')}
              value={exchangeOrder.order.transaction_id}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.row-start')}
              value={exchangeOrder.order.row_start}
              formattedValue={UTCToDateTimeWithSecond(exchangeOrder.order.row_start as string)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.validity')}
              value={exchangeOrder.order.validity}
              formattedValue={UTCToDateTimeWithSecond(exchangeOrder.order.validity as string)}
              nameSx={{ width: '130px' }}
            />

            <InfoField
              name={t('transaction.order.data.title')}
              // @ts-ignore
              value={exchangeOrder.order.data}
              maxWidthValue={'500px'}
              component={
                <Stack direction="column" spacing={1.5}>
                  {exchangeOrder.order.data ? renderObject(exchangeOrder.order.data) : '–'}
                </Stack>
              }
              withoutDivider
            />
          </Paper>
        </Grid>
      )}

      {exchangeOrder?.order?.profitLoss && (
        <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1.6 } }}>
          <Paper sx={{ p: '24px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography pb={2}>{t('transaction.label.profit')}</Typography>

            {exchangeOrder.order.profitLoss.map((item, index) => {
              return (
                <InfoField
                  key={index}
                  name={item.name}
                  value={undefined}
                  withEmptyValue
                  formattedValue={formatFiatAmountWithCurrency(item.amount, item.coinCode)}
                  nameSx={{ width: '130px' }}
                />
              );
            })}
          </Paper>
        </Grid>
      )}
      {transaction?.transaction.payment_method === 'BLOCKCHAIN' && hasPermissionToReadAmlChecks && (
        <AmlBlock transaction={transaction?.transaction} />
      )}

      <ConfirmModal
        open={confirmModalShown}
        onClose={closeConfirmModal}
        confirmAction={activeConfirmAction}
        onSuccess={executeAction}
        onCancel={closeConfirmModal}
        isLoading={isLoading}
        withReason={activeConfirmAction === 'cancel'}
      />

      <UploadDocumentModal
        open={uploadModalShown}
        onClose={closeUploadModal}
        onUpload={uploadDocument}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isDocumentUploadLoading}
      />

      <ViewDocumentModal
        onClose={() => setOpenViewDocument(false)}
        item={selectredFile}
        open={openViewDocument}
      />

      <DeleteConfirmModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onSuccess={handleDeleteDocument}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      />

      <AmlConfirmModal
        open={amlConfirmModalShown}
        onClose={closeAmlConfirmModal}
        confirmAction={activeConfirmAction}
        onSuccess={executeAmlAction}
        onCancel={closeAmlConfirmModal}
        isLoading={isLoading}
      />
    </>
  );
};
